import {
  sessionlessEvidenceAwarenessDevForceShowAtom,
  sessionlessEvidenceAwarenessDevVariantOverrideAtom,
} from '@/atoms/sessionlessEvidenceAwarenessDevAtoms';

import {
  GET_EVIDENCE_AWARENESS_POPUP_VARIANT,
  IS_CONSOLIDATED_SESSIONS_LIST_ENABLED,
} from '@/components/constants/flags';
import { useShowSessionsListAssistantView } from '@/components/layout/Navigation/sessions/hooks/useShowSessionsListAssistantView';
import { SessionsList } from '@/components/layout/Navigation/sessions/side-nav-list/components/sessions-list';
import { SessionsListAssistantView } from '@/components/layout/Navigation/sessions/side-nav-list/components/sessions-list-assistant-view';
import { SessionsListEmpty } from '@/components/layout/Navigation/sessions/side-nav-list/components/sessions-list-empty';
import { SessionsListError } from '@/components/layout/Navigation/sessions/side-nav-list/components/sessions-list-error';
import { SessionsListFooter } from '@/components/layout/Navigation/sessions/side-nav-list/components/sessions-list-footer';
import { SessionsListLoading } from '@/components/layout/Navigation/sessions/side-nav-list/components/sessions-list-loading';
import { TidyUpButton } from '@/components/layout/Navigation/sessions/side-nav-list/components/tidy-up-button';
import { useSessionsList } from '@/components/layout/Navigation/sessions/side-nav-list/hooks/useSessionsList';
import { SessionlessEvidenceAwarenessPopover } from '@/components/modal/sessionless-evidence-awareness-popover';
import { cn } from '@/lib/cn';
import { useAtomValue } from 'jotai';
import { isEmpty } from 'lodash-es';
import { memo, useMemo } from 'react';
import { useSessionsFilteredList } from '../hooks/useSessionsFilteredList';

export const SideNavSessionsList = memo(() => {
  useAtomValue(sessionlessEvidenceAwarenessDevVariantOverrideAtom);
  useAtomValue(sessionlessEvidenceAwarenessDevForceShowAtom);
  const isConsolidatedSessionsEnabled = IS_CONSOLIDATED_SESSIONS_LIST_ENABLED();
  const { sessionSearchResult } = useSessionsFilteredList();
  const { isError, isLoading, isFetchingNextPage } = useSessionsList();

  const showSessionsListLoading = useMemo(() => {
    return isLoading && !isFetchingNextPage;
  }, [isLoading, isFetchingNextPage]);

  const showSessionsListAssistantView = useShowSessionsListAssistantView();

  const showSidebarEvidenceAwareness =
    GET_EVIDENCE_AWARENESS_POPUP_VARIANT() === 'sidebar';

  if (showSessionsListAssistantView) {
    return <SessionsListAssistantView />;
  }

  return (
    <div className="h-full" data-testid="sidebar-session-list-container">
      <div className={cn('flex h-full min-h-0 flex-col gap-2 rounded-md')}>
        <div className={cn('min-h-0 flex-1 overflow-x-hidden')}>
          {showSessionsListLoading ? (
            <SessionsListLoading />
          ) : isError ? (
            <SessionsListError />
          ) : (
            <div className="flex h-full flex-col gap-1">
              <>
                {!isEmpty(sessionSearchResult) && sessionSearchResult ? (
                  <SessionsList />
                ) : (
                  <SessionsListEmpty />
                )}
              </>
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col gap-2">
          {!isConsolidatedSessionsEnabled && <TidyUpButton />}
          <SessionsListFooter />
          {showSidebarEvidenceAwareness ? (
            <SessionlessEvidenceAwarenessPopover variant="sidebar" />
          ) : null}
        </div>
      </div>
    </div>
  );
});

SideNavSessionsList.displayName = 'SideNavSessionsListV2';
